import { useEffect, useState, type FormEvent } from 'react';
import { v } from '../constants';
import { Wordmark } from './ui';
import { apiFetch } from '../lib/api';

// /reset-password?token=X — the email link lands here.
//
// Three states:
//  1) Form: token present, no submission yet → show new-password form.
//  2) Success: server accepted token + password change → "Sign in"
//     CTA (we intentionally do NOT auto-log-in — the server revokes
//     all sessions on reset so the user re-authenticates fresh).
//  3) Error: missing token, invalid/expired token, password too short.

const ERROR_COPY: Record<string, string> = {
  invalid_or_expired_token: "This reset link is expired or already used. Request a new one from the sign-in screen.",
  invalid_input: 'Password must be at least 8 characters.',
  rate_limited: 'Too many attempts. Try again in a few minutes.',
};

export function ResetPasswordPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Pull the body-scroll lift class so this page acts like the other
  // long-form surfaces (legal pages, profile). Without it, the global
  // `body { overflow: hidden }` would prevent any vertical scroll on
  // tall enough viewports.
  useEffect(() => {
    document.body.classList.add('hm-page-scroll');
    return () => { document.body.classList.remove('hm-page-scroll'); };
  }, []);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await apiFetch('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      if (r.ok) {
        setSuccess(true);
      } else {
        const body = await r.json().catch(() => ({}));
        setError(ERROR_COPY[body.error] ?? body.error ?? 'Something went wrong. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <Shell title="Reset password">
        <p style={{ color: v('inkSoft') }}>
          This page needs a token from the email we sent. Request a new
          link from <a href="/" style={linkStyle()}>the sign-in screen</a>.
        </p>
      </Shell>
    );
  }

  if (success) {
    return (
      <Shell title="Password reset">
        <p style={{ color: v('inkSoft'), lineHeight: 1.5 }}>
          Your password has been updated. For your security we signed
          you out of all devices — please sign in with your new password.
        </p>
        <a href="/" className="hm-primary" style={{ display: 'inline-block', marginTop: 16, textDecoration: 'none' }}>
          Go to sign in
        </a>
      </Shell>
    );
  }

  return (
    <Shell title="Reset password">
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: v('inkSoft') }}>New password</span>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            style={inputStyle()}
          />
          <span style={{ fontSize: 11, color: v('inkMute') }}>At least 8 characters.</span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: v('inkSoft') }}>Confirm password</span>
          <input
            type="password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            style={inputStyle()}
          />
        </label>
        {error && (
          <div style={{
            padding: '8px 12px',
            borderRadius: 8,
            background: v('paperDeep'),
            color: v('bad'),
            fontSize: 13,
            border: `1px solid ${v('bad')}`,
          }}>{error}</div>
        )}
        <div style={{ marginTop: 6 }}>
          <button type="submit" className="hm-primary" disabled={submitting}>
            {submitting ? 'Resetting…' : 'Reset password'}
          </button>
        </div>
      </form>
    </Shell>
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      minHeight: '100dvh',
      background: v('paper'),
      color: v('ink'),
    }}>
      <header style={{
        padding: '18px 22px',
        borderBottom: `1px solid ${v('ruleSoft')}`,
      }}>
        <a href="/" style={{ textDecoration: 'none', color: 'inherit', display: 'inline-block' }}>
          <Wordmark />
        </a>
      </header>
      <main style={{
        maxWidth: 420,
        margin: '0 auto',
        padding: '40px 22px 60px',
      }}>
        <h1 style={{ margin: '0 0 12px', fontSize: 26, fontWeight: 600 }}>{title}</h1>
        {children}
      </main>
    </div>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    fontFamily: 'inherit',
    fontSize: 14,
    padding: '8px 12px',
    border: `1px solid ${v('rule')}`,
    borderRadius: 8,
    background: v('paper'),
    color: v('ink'),
    outline: 'none',
  };
}

function linkStyle(): React.CSSProperties {
  return { color: v('ink'), textDecoration: 'underline' };
}
